import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { User, Lock, ArrowRight, ShieldAlert, Mail, Loader2, KeyRound } from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext'; 

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api';

const Login = () => {

  const navigate = useNavigate();

  const { addNotification } = useNotification();

  const { login } = useAuth();



  // State Management

  const [mode, setMode] = useState('login'); // 'login', 'forgot', 'reset'

  const [loading, setLoading] = useState(false);



  const [formData, setFormData] = useState({

    email: '',

    password: '',

    otp: '',

    newPassword: ''

  });



  // --- Security & SEO (No-Index) ---

  React.useEffect(() => {

    document.title = "Admin Access | iMoonusel";

    let meta = document.querySelector('meta[name="robots"]');

    if (!meta) {

      meta = document.createElement('meta');

      meta.name = "robots";

      document.head.appendChild(meta);

    }

    meta.content = "noindex, nofollow";



    return () => {

      // Re-enable indexing for other pages when leaving

      if (meta) meta.content = "index, follow";

    };

  }, []);



  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // 1. Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        addNotification('Login berhasil! Selamat datang.', 'success');
        navigate('/admin/dashboard');
      } else {
        addNotification(result.message, 'error');
      }
    } catch (err) {
      addNotification('Connection error. Please check your server or network.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 2. Handle Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!formData.email) {
      addNotification('Please enter your email first.', 'error');
      setLoading(false);
      return;
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot_password.php`, {
        email: formData.email
      });
      addNotification(response.data.message, 'success');
      setMode('reset'); // Switch to Reset/Verify Mode
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to request password reset.';
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 3. Handle Verify & Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/reset_password.php`, {
        email: formData.email,
        otp: formData.otp,
        new_password: formData.newPassword
      });
      addNotification(response.data.message, 'success');
      setMode('login'); // Back to Login
      setFormData({ ...formData, password: '', otp: '', newPassword: '' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to reset password.';
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f3f4f6', 
    },
    card: {
      backgroundColor: '#fff',
      padding: '2.5rem',
      borderRadius: '1rem',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '400px',
    },
    header: {
      textAlign: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#111',
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    inputGroup: {
      marginBottom: '1.5rem',
      position: 'relative',
    },
    input: {
      width: '100%',
      padding: '0.75rem 0.75rem 0.75rem 2.5rem', 
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      outline: 'none',
      transition: 'border-color 0.2s',
    },
    icon: {
      position: 'absolute',
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
      width: '1.2rem',
      height: '1.2rem',
    },
    button: {
      width: '100%',
      padding: '0.75rem',
      backgroundColor: '#111',
      color: '#fff',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      opacity: loading ? 0.7 : 1,
    },
    linkBtn: {
      background: 'none',
      border: 'none',
      color: '#2563eb',
      fontSize: '0.875rem',
      cursor: 'pointer',
      marginTop: '1rem',
      display: 'block',
      marginLeft: 'auto',
    }
  };

  // Determine which function to call
  const handleSubmit = (e) => {
    if (mode === 'login') handleLogin(e);
    else if (mode === 'forgot') handleRequestOTP(e);
    else if (mode === 'reset') handleResetPassword(e);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            {mode === 'login' && 'Admin Login'}
            {mode === 'forgot' && 'Reset Password'}
            {mode === 'reset' && 'Verify OTP'}
          </h1>
          <p style={styles.subtitle}>
            {mode === 'login' && 'Enter your credentials to access the panel'}
            {mode === 'forgot' && 'Enter your email to receive an OTP'}
            {mode === 'reset' && 'Check your email for the code'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email Field - Always Visible except maybe in future if we want to hide it */}
          <div style={styles.inputGroup}>
            <Mail style={styles.icon} />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
              required
              readOnly={mode === 'reset'} // Lock email during reset
            />
          </div>

          {/* Login Password */}
          {mode === 'login' && (
            <div style={styles.inputGroup}>
              <Lock style={styles.icon} />
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>
          )}

          {/* Reset Fields */}
          {mode === 'reset' && (
            <>
              <div style={styles.inputGroup}>
                <KeyRound style={styles.icon} />
                <input
                  type="text"
                  name="otp"
                  placeholder="Enter OTP Code"
                  value={formData.otp}
                  onChange={handleChange}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.inputGroup}>
                <Lock style={styles.icon} />
                <input
                  type="password"
                  name="newPassword"
                  placeholder="New Password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  style={styles.input}
                  required
                />
              </div>
            </>
          )}

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? <Loader2 className="animate-spin" size={20} /> : (
              <>
                {mode === 'login' && 'Sign In'}
                {mode === 'forgot' && 'Send OTP'}
                {mode === 'reset' && 'Change Password'}
                {!loading && <ArrowRight size={18} />}
              </>
            )}
          </button>
        </form>

        <button 
          type="button" 
          style={styles.linkBtn}
          onClick={() => {
            if (mode === 'login') setMode('forgot');
            else setMode('login');
          }}
        >
          {mode === 'login' ? 'Forgot Password?' : 'Back to Login'}
        </button>
      </div>
    </div>
  );
};

export default Login;

